/**
 * Seed content for the service pages that used to live in the legacy,
 * form-only `services` CMS section (serviceData.js / serviceDetailContent.js)
 * and render through the hardcoded ServiceDetail.jsx template. Migrated into
 * the richer, fully inline-WYSIWYG `servicePages` system (TemplateServicePage)
 * so every service page — not just Full-Stack & SaaS Development — is fully
 * editable from the "Service Pages" admin tab.
 *
 * Shape matches DEFAULT_PAGE_CONTENT in servicePageTemplate.js exactly.
 */

const bookCta = { label: 'Book a Free 30-Min Consultation', to: '/book-consultation' };

function glanceRow(icon, label, value) {
  return { icon, label, value };
}

export const LEGACY_SERVICE_PAGES = [
  {
    slug: 'ai-chatbots',
    title: 'AI Chatbots',
    data: {
      meta: { documentTitle: 'AI Chatbot Development Services | Databiqs' },
      hiddenSections: [],
      cardPreview: {
        icon: 'bot',
        shortDescription: 'Build custom conversational AI chatbots that understand context, resolve complex customer queries, provide 24/7 support, and integrate with your CRM and business systems.',
        features: ['Conversational AI', 'Customer Support Automation', 'CRM Integration'],
      },
      hero: {
        kicker: 'Conversational AI',
        breadcrumbCurrent: 'AI Chatbots',
        title: 'AI Chatbots That Convert And Retain',
        lead: 'We design AI chatbot systems that feel human, resolve complex customer queries, provide 24/7 support, and integrate directly with your CRM and business systems — tailored to your brand voice and outcomes.',
        primaryCtaLabel: bookCta.label,
        primaryCtaTo: bookCta.to,
        secondaryCtaLabel: 'See case studies',
        secondaryCtaTo: '/case-studies',
        tags: ['Conversational AI', 'Customer Support Automation', 'CRM Integration', 'Lead Qualification', '24/7 Coverage'],
        glance: {
          statusLabel: 'Taking new projects',
          rows: [
            glanceRow('clock', 'Launch timeline', '[4–8] weeks'),
            glanceRow('bolt', 'Projects start from', 'USD [X]'),
            glanceRow('users', 'Engagement', 'Fixed price · Dedicated team'),
            glanceRow('shield', 'Confidentiality', 'NDA on request'),
          ],
          ctaLabel: 'Get a Free Estimate',
        },
      },
      whatWeBuild: {
        kicker: 'What we build',
        title: 'Conversations that resolve, qualify and convert',
        side: 'One team covers conversation design, model training, integrations and analytics.',
        cards: [
          { icon: 'message', title: 'Conversational AI', desc: 'Chatbots that understand context and intent, not just keywords.' },
          { icon: 'bot', title: 'Customer support automation', desc: 'Resolve repetitive queries instantly while escalating complex ones to humans.' },
          { icon: 'plug', title: 'CRM & helpdesk integration', desc: 'Connect to Salesforce, HubSpot, Zendesk and your internal tools.' },
          { icon: 'target', title: 'Lead qualification', desc: 'Intent-driven flows that qualify and route leads to sales in real time.' },
          { icon: 'shieldCheck', title: 'Guardrails & safety', desc: 'Scoped responses, escalation paths and human-in-the-loop review.' },
          { icon: 'chip', title: 'Multi-channel deployment', desc: 'Web, WhatsApp, Slack and mobile, from a single conversation engine.' },
        ],
      },
      whyDatabiqs: {
        kicker: 'Why Databiqs',
        title: 'Why teams choose Databiqs for AI chatbots',
        cards: [
          { icon: 'brain', title: 'Context-aware by design', desc: 'Chatbots that remember the conversation, not just the last message.' },
          { icon: 'layers', title: 'One team, full stack', desc: 'Conversation design, model training and integrations — no hand-offs.' },
          { icon: 'users', title: 'Scales with you', desc: 'Start fixed-price, move to a dedicated team as usage grows.' },
          { icon: 'code', title: 'Clean, owned code', desc: 'Tested, documented integrations you keep in your own repositories.' },
        ],
      },
      toc: {
        eyebrow: 'On this page',
        items: [
          { id: 'chat-end-to-end', label: 'AI chatbot development, end to end' },
          { id: 'chat-timeline-cost', label: 'Chatbot launch: timeline and cost' },
          { id: 'chat-hire-team', label: 'Fixed-price build or a dedicated AI team' },
          { id: 'chat-integrations', label: 'CRM, helpdesk and channel integrations' },
        ],
        cardTitle: 'Need a quote?',
        cardDesc: 'Free 30-minute call, fixed quote after.',
        cardCtaLabel: 'Book a call',
        cardCtaTo: '/book-consultation',
      },
      longform: [
        {
          id: 'chat-end-to-end',
          title: 'AI chatbot development, end to end',
          paragraphs: [
            'We design the conversation first — intents, edge cases and escalation paths — then train and integrate the model around that design, so the chatbot behaves predictably in production, not just in a demo.',
            'Every chatbot ships with analytics on intent coverage and resolution rate, so you can see exactly where it helps and where it still needs a human.',
          ],
        },
        {
          id: 'chat-timeline-cost',
          title: 'Chatbot launch: timeline and cost',
          paragraphs: [
            'A focused support or lead-qualification chatbot typically launches in [4–8] weeks and costs USD [X]–[Y], depending on the number of intents, integrations and languages required.',
          ],
        },
        {
          id: 'chat-hire-team',
          title: 'Fixed-price build or a dedicated AI team',
          paragraphs: [
            'A fixed-price engagement suits a clearly scoped first chatbot. A dedicated team suits products where conversational AI keeps expanding — new intents, new channels, new integrations — month after month.',
          ],
        },
        {
          id: 'chat-integrations',
          title: 'CRM, helpdesk and channel integrations',
          paragraphs: [
            'We connect chatbots to the systems your team already uses — CRM, helpdesk, booking and billing — so conversations turn into logged, actionable records instead of a separate silo.',
          ],
        },
      ],
      process: {
        kicker: 'How we work',
        title: 'A clear process, with a demo every week',
        ctaLabel: bookCta.label,
        ctaTo: bookCta.to,
        steps: [
          { num: '01', week: 'Week 1', title: 'Discovery', desc: 'Map intents, customer journeys and systems to integrate with.' },
          { num: '02', week: 'Weeks 2–3', title: 'Conversation design', desc: 'Flows, guardrails and escalation paths you review before training.' },
          { num: '03', week: 'Weeks 4–6', title: 'Build & train', desc: 'Model training and integrations, with a live demo every Friday.' },
          { num: '04', week: 'Ongoing', title: 'Launch & tune', desc: 'Launch, monitor intent coverage and keep improving resolution rate.' },
        ],
      },
      techStack: {
        kicker: 'Tech stack',
        title: 'Built on tools that scale',
        items: [
          { name: 'Python', logoKey: 'python' },
          { name: 'Node.js', logoKey: 'nodejs' },
          { name: 'PostgreSQL', logoKey: 'postgresql' },
          { name: 'AWS', logoKey: 'aws' },
          { name: 'Docker', logoKey: 'docker' },
        ],
      },
      caseStudy: {
        kicker: 'Featured case study',
        imageSrc: '',
        imageAlt: 'Product screenshot — AI chatbot built by Databiqs',
        projectName: '[Project name]',
        meta: 'Support chatbot · CRM integration',
        sub: '[Industry] company, [size] — named with permission, or "name withheld under NDA".',
        challengeLabel: 'Challenge',
        challengeText: '[The problem the client had, in one or two sentences.]',
        solutionLabel: 'What we built',
        solutionText: '[The solution, stack and timeline.]',
        results: [
          { value: '[Result]', label: 'e.g. first-response time reduced' },
          { value: '[Result]', label: 'e.g. launched in [X] weeks' },
        ],
        linkLabel: 'Read the full case study',
        linkTo: '/case-studies',
      },
      engagement: {
        kicker: 'Engagement models',
        title: 'Pick how you want to work with us',
        noteText: 'Chatbot projects typically start from USD [X]',
        tiers: [
          { icon: 'file', title: 'Fixed-price project', desc: 'A defined chatbot scope with a fixed quote after a free 30-minute call.', bullets: ['Fixed scope and timeline', 'Weekly demos', 'Pay by milestone'], highlight: false, tag: '' },
          { icon: 'users', title: 'Dedicated AI team', desc: 'Senior engineers who keep expanding your conversational AI, billed monthly.', bullets: ['New intents and channels', 'Works in your tools', 'Monthly billing'], highlight: true, tag: 'Scale with us' },
          { icon: 'clock', title: 'Hourly', desc: 'For audits, tuning and ongoing support after launch.', bullets: ['No minimum commitment', 'Transparent time logs', 'Senior engineers only'], highlight: false, tag: '' },
        ],
      },
      faq: {
        kicker: 'FAQ',
        title: 'Questions, answered',
        lead: 'Still have a question? Ask us directly on a free 30-minute call.',
        ctaLabel: bookCta.label,
        ctaTo: bookCta.to,
        items: [
          { question: 'How much does an AI chatbot cost?', answer: 'Most chatbots we build cost between USD [X] and USD [Y], depending on intents, integrations and channels. You get a fixed quote after a free 30-minute call.' },
          { question: 'Will it integrate with our CRM or helpdesk?', answer: 'Yes — we regularly integrate with Salesforce, HubSpot, Zendesk and most CRMs and helpdesks with an API.' },
          { question: 'How fast can you start?', answer: 'Most engagements kick off within [1–2] weeks of the discovery call, once intents and scope are signed off.' },
          { question: 'Fixed price or dedicated team — which is right for me?', answer: 'Choose fixed price for a clearly scoped first chatbot. Choose a dedicated team if your conversational AI will keep growing after launch.' },
          { question: 'Who owns the code and the trained flows?', answer: 'You do. All code, conversation flows and documentation are handed over and live in your own repositories.' },
          { question: 'Can it support multiple languages?', answer: 'Yes — multi-language support is scoped during discovery and reflected in the timeline and quote.' },
        ],
      },
      related: {
        kicker: 'Related services',
        title: 'Explore more ways we can help',
        items: [
          { icon: 'workflow', title: 'AI Automation', desc: 'Workflow orchestration and process automation', to: '/services/ai-automation' },
          { icon: 'chip', title: 'Machine Learning', desc: 'Predictive models trained on your data', to: '/services/machine-learning' },
          { icon: 'calendar', title: 'Free consultation', desc: '30 minutes with a senior engineer', to: '/book-consultation' },
        ],
      },
      blog: {
        kicker: 'From the blog',
        title: 'Guides and answers',
        allLabel: 'All articles',
        allTo: '/blog-page',
        posts: [
          { tag: 'Chatbots', cover: 'Chatbot ROI', imageSrc: '', title: 'How to measure chatbot ROI', to: '/blog-page', readTime: '[X] min read', linkLabel: 'Read the guide' },
          { tag: 'CX', cover: 'support automation', imageSrc: '', title: 'When to automate customer support', to: '/blog-page', readTime: '[X] min read', linkLabel: 'Read the guide' },
          { tag: 'AI', cover: 'conversation design', imageSrc: '', title: 'Conversation design basics for AI chatbots', to: '/blog-page', readTime: '[X] min read', linkLabel: 'Read the guide' },
        ],
      },
      finalCta: {
        kicker: 'Start a project',
        title: 'Plan your AI chatbot with us',
        lead: 'Share a few details and a senior engineer will reply within 24 hours.',
        bullets: ['Fixed quote after a free 30-minute call', 'NDA on request', 'You own 100% of the code'],
        form: {
          badgeText: 'Senior engineer reviews every request',
          title: 'Get a free project estimate',
          sub: 'Tell us a little about your project. We reply within 24 hours.',
          submitLabel: 'Get my free estimate',
          privacyText: 'Your details stay confidential and are never shared or sold. NDA available on request.',
          fields: [
            { key: 'fullName', type: 'text', label: 'Full name', placeholder: 'Jane Smith', required: true },
            { key: 'email', type: 'email', label: 'Work email', placeholder: 'jane@company.com', required: true },
            { key: 'company', type: 'text', label: 'Company name', placeholder: 'Company Inc.', required: false },
            { key: 'phone', type: 'tel', label: 'Phone number', placeholder: '+1 555 000 0000', required: false },
            { key: 'services', type: 'select', label: 'Services', required: false, options: [
              { value: 'Full-Stack & SaaS Development', label: 'Full-Stack & SaaS Development' },
              { value: 'AI Chatbots', label: 'AI Chatbots' },
              { value: 'AI Automation', label: 'AI Automation' },
              { value: 'Machine Learning', label: 'Machine Learning' },
              { value: 'AI Consulting', label: 'AI Consulting' },
              { value: 'Mobile App Development', label: 'Mobile App Development' },
              { value: 'Digital Marketing', label: 'Digital Marketing' },
              { value: 'Graphic Designing', label: 'Graphic Designing' },
              { value: 'UI/UX Designing', label: 'UI/UX Designing' },
              { value: 'Not sure yet', label: 'Not sure yet' },
            ] },
          ],
        },
      },
    },
  },
  {
    slug: 'ai-automation',
    title: 'AI Automation',
    data: {
      meta: { documentTitle: 'AI Automation Services | Databiqs' },
      hiddenSections: [],
      cardPreview: {
        icon: 'workflow',
        shortDescription: 'End-to-end intelligent process automation. Streamline operations, reduce manual errors, and accelerate throughput.',
        features: ['Workflow Orchestration', 'Data Extraction (IDP)', 'System Integrations'],
      },
      hero: {
        kicker: 'Automation Excellence',
        breadcrumbCurrent: 'AI Automation',
        title: 'Automate Complex Operations With Precision',
        lead: 'End-to-end intelligent process automation. We identify high-impact bottlenecks, design resilient automation pipelines, and deploy systems that streamline operations and scale without compromising quality.',
        primaryCtaLabel: bookCta.label,
        primaryCtaTo: bookCta.to,
        secondaryCtaLabel: 'See case studies',
        secondaryCtaTo: '/case-studies',
        tags: ['Workflow Orchestration', 'Data Extraction (IDP)', 'System Integrations', 'Human-in-the-loop QA', 'Monitoring & Alerting'],
        glance: {
          statusLabel: 'Taking new projects',
          rows: [
            glanceRow('clock', 'Launch timeline', '[6–10] weeks'),
            glanceRow('bolt', 'Projects start from', 'USD [X]'),
            glanceRow('users', 'Engagement', 'Fixed price · Dedicated team'),
            glanceRow('shield', 'Confidentiality', 'NDA on request'),
          ],
          ctaLabel: 'Get a Free Estimate',
        },
      },
      whatWeBuild: {
        kicker: 'What we build',
        title: 'From manual workflows to resilient automation pipelines',
        side: 'One team covers process discovery, orchestration, integrations and monitoring.',
        cards: [
          { icon: 'workflow', title: 'Workflow orchestration', desc: 'Rule + AI powered pipelines that coordinate multi-step business processes.' },
          { icon: 'file', title: 'Data extraction (IDP)', desc: 'Pull structured data from documents, emails and forms automatically.' },
          { icon: 'plug', title: 'System integrations', desc: 'Connect ERPs, CRMs and internal tools into one automated flow.' },
          { icon: 'shieldCheck', title: 'Human-in-the-loop checkpoints', desc: 'Quality gates where a person reviews before high-stakes actions.' },
          { icon: 'search', title: 'Monitoring & alerting', desc: 'Catch failures and drift before they become operational incidents.' },
          { icon: 'cloud', title: 'Cloud-native deployment', desc: 'Scalable infrastructure on AWS with CI/CD and observability.' },
        ],
      },
      whyDatabiqs: {
        kicker: 'Why Databiqs',
        title: 'Why operations teams choose Databiqs for automation',
        cards: [
          { icon: 'bolt', title: 'Built for throughput', desc: 'Automation designed around your actual bottlenecks, not generic templates.' },
          { icon: 'layers', title: 'One team, full stack', desc: 'Process discovery, engineering and monitoring — no hand-offs.' },
          { icon: 'users', title: 'Scales with you', desc: 'Start with one workflow, expand to a dedicated automation team.' },
          { icon: 'code', title: 'Clean, owned code', desc: 'Tested, documented pipelines you keep in your own infrastructure.' },
        ],
      },
      toc: {
        eyebrow: 'On this page',
        items: [
          { id: 'auto-end-to-end', label: 'AI automation services, end to end' },
          { id: 'auto-timeline-cost', label: 'Automation rollout: timeline and cost' },
          { id: 'auto-hire-team', label: 'Fixed-price project or a dedicated automation team' },
          { id: 'auto-integrations', label: 'Integrating with your ERP, CRM and internal tools' },
        ],
        cardTitle: 'Need a quote?',
        cardDesc: 'Free 30-minute call, fixed quote after.',
        cardCtaLabel: 'Book a call',
        cardCtaTo: '/book-consultation',
      },
      longform: [
        {
          id: 'auto-end-to-end',
          title: 'AI automation services, end to end',
          paragraphs: [
            'We start with an operational discovery to find the workflows costing you the most manual effort, then design automation pipelines with human-in-the-loop checkpoints so quality never drops as volume grows.',
            'We work in weekly sprints and demo working automation every week, so you can validate behavior on real cases before it touches production volume.',
          ],
        },
        {
          id: 'auto-timeline-cost',
          title: 'Automation rollout: timeline and cost',
          paragraphs: [
            'A focused first workflow typically rolls out in [6–10] weeks and costs USD [X]–[Y], depending on the number of systems it touches and how much data extraction is involved.',
          ],
        },
        {
          id: 'auto-hire-team',
          title: 'Fixed-price project or a dedicated automation team',
          paragraphs: [
            'A fixed-price engagement suits one clearly scoped workflow. A dedicated automation team suits organizations automating multiple processes across departments over time.',
          ],
        },
        {
          id: 'auto-integrations',
          title: 'Integrating with your ERP, CRM and internal tools',
          paragraphs: [
            'Automation is only useful if it talks to the systems you already run on. We integrate with ERPs, CRMs, helpdesks and internal tools via API, so automated steps update the same records your team already trusts.',
          ],
        },
      ],
      process: {
        kicker: 'How we work',
        title: 'A clear process, with a demo every week',
        ctaLabel: bookCta.label,
        ctaTo: bookCta.to,
        steps: [
          { num: '01', week: 'Week 1', title: 'Operational discovery', desc: 'Identify automation-ready workflows and constraints.' },
          { num: '02', week: 'Weeks 2–3', title: 'Pipeline design', desc: 'Workflow engineering with human-in-the-loop checkpoints you approve.' },
          { num: '03', week: 'Weeks 4–8', title: 'Build & integrate', desc: 'Senior engineers ship in weekly sprints, with a live demo every Friday.' },
          { num: '04', week: 'Ongoing', title: 'Monitor & tune', desc: 'Monitoring, alerting and iterative tuning for long-term performance.' },
        ],
      },
      techStack: {
        kicker: 'Tech stack',
        title: 'Built on tools that scale',
        items: [
          { name: 'Python', logoKey: 'python' },
          { name: 'Node.js', logoKey: 'nodejs' },
          { name: 'PostgreSQL', logoKey: 'postgresql' },
          { name: 'AWS', logoKey: 'aws' },
          { name: 'Docker', logoKey: 'docker' },
        ],
      },
      caseStudy: {
        kicker: 'Featured case study',
        imageSrc: '',
        imageAlt: 'Product screenshot — automation pipeline built by Databiqs',
        projectName: '[Project name]',
        meta: 'Operations automation · IDP + ERP integration',
        sub: '[Industry] company, [size] — named with permission, or "name withheld under NDA".',
        challengeLabel: 'Challenge',
        challengeText: '[The problem the client had, in one or two sentences.]',
        solutionLabel: 'What we built',
        solutionText: '[The solution, stack and timeline.]',
        results: [
          { value: '[Result]', label: 'e.g. manual hours saved per week' },
          { value: '[Result]', label: 'e.g. error rate reduced' },
        ],
        linkLabel: 'Read the full case study',
        linkTo: '/case-studies',
      },
      engagement: {
        kicker: 'Engagement models',
        title: 'Pick how you want to work with us',
        noteText: 'Automation projects typically start from USD [X]',
        tiers: [
          { icon: 'file', title: 'Fixed-price project', desc: 'One defined workflow automated end to end, with a fixed quote after a free call.', bullets: ['Fixed scope and timeline', 'Weekly demos', 'Pay by milestone'], highlight: false, tag: '' },
          { icon: 'users', title: 'Dedicated automation team', desc: 'Senior engineers who keep automating processes across departments, billed monthly.', bullets: ['New workflows added over time', 'Works in your tools', 'Monthly billing'], highlight: true, tag: 'Scale with us' },
          { icon: 'clock', title: 'Hourly', desc: 'For audits, tuning and ongoing support after rollout.', bullets: ['No minimum commitment', 'Transparent time logs', 'Senior engineers only'], highlight: false, tag: '' },
        ],
      },
      faq: {
        kicker: 'FAQ',
        title: 'Questions, answered',
        lead: 'Still have a question? Ask us directly on a free 30-minute call.',
        ctaLabel: bookCta.label,
        ctaTo: bookCta.to,
        items: [
          { question: 'How much does AI automation cost?', answer: 'Most automation projects cost between USD [X] and USD [Y], depending on the number of systems and amount of data extraction involved. You get a fixed quote after a free call.' },
          { question: 'What kinds of workflows can you automate?', answer: 'Document processing, approvals, data entry between systems, reporting and most repetitive multi-step operational workflows.' },
          { question: 'How do you keep quality high as volume scales?', answer: 'Human-in-the-loop checkpoints review high-stakes or low-confidence cases, with monitoring and alerting to catch drift early.' },
          { question: 'Fixed price or dedicated team — which is right for me?', answer: 'Choose fixed price for one clearly scoped workflow. Choose a dedicated team if you plan to automate multiple processes over time.' },
          { question: 'Who owns the automation pipelines?', answer: 'You do. All code, infrastructure and documentation are handed over and live in your own environment.' },
          { question: 'How fast can you start?', answer: 'Most engagements kick off within [1–2] weeks of the discovery call, once scope is signed off.' },
        ],
      },
      related: {
        kicker: 'Related services',
        title: 'Explore more ways we can help',
        items: [
          { icon: 'message', title: 'AI Chatbots', desc: 'Conversational AI for support and sales', to: '/services/ai-chatbots' },
          { icon: 'chip', title: 'Machine Learning', desc: 'Predictive models trained on your data', to: '/services/machine-learning' },
          { icon: 'calendar', title: 'Free consultation', desc: '30 minutes with a senior engineer', to: '/book-consultation' },
        ],
      },
      blog: {
        kicker: 'From the blog',
        title: 'Guides and answers',
        allLabel: 'All articles',
        allTo: '/blog-page',
        posts: [
          { tag: 'Automation', cover: 'process automation ROI', imageSrc: '', title: 'How to prioritize workflows for automation', to: '/blog-page', readTime: '[X] min read', linkLabel: 'Read the guide' },
          { tag: 'Operations', cover: 'IDP basics', imageSrc: '', title: 'Intelligent document processing, explained', to: '/blog-page', readTime: '[X] min read', linkLabel: 'Read the guide' },
          { tag: 'AI', cover: 'human in the loop', imageSrc: '', title: 'Why human-in-the-loop still matters', to: '/blog-page', readTime: '[X] min read', linkLabel: 'Read the guide' },
        ],
      },
      finalCta: {
        kicker: 'Start a project',
        title: 'Plan your automation rollout with us',
        lead: 'Share a few details and a senior engineer will reply within 24 hours.',
        bullets: ['Fixed quote after a free 30-minute call', 'NDA on request', 'You own 100% of the code'],
        form: {
          badgeText: 'Senior engineer reviews every request',
          title: 'Get a free project estimate',
          sub: 'Tell us a little about your project. We reply within 24 hours.',
          submitLabel: 'Get my free estimate',
          privacyText: 'Your details stay confidential and are never shared or sold. NDA available on request.',
          fields: [
            { key: 'fullName', type: 'text', label: 'Full name', placeholder: 'Jane Smith', required: true },
            { key: 'email', type: 'email', label: 'Work email', placeholder: 'jane@company.com', required: true },
            { key: 'company', type: 'text', label: 'Company name', placeholder: 'Company Inc.', required: false },
            { key: 'phone', type: 'tel', label: 'Phone number', placeholder: '+1 555 000 0000', required: false },
            { key: 'services', type: 'select', label: 'Services', required: false, options: [
              { value: 'Full-Stack & SaaS Development', label: 'Full-Stack & SaaS Development' },
              { value: 'AI Chatbots', label: 'AI Chatbots' },
              { value: 'AI Automation', label: 'AI Automation' },
              { value: 'Machine Learning', label: 'Machine Learning' },
              { value: 'AI Consulting', label: 'AI Consulting' },
              { value: 'Mobile App Development', label: 'Mobile App Development' },
              { value: 'Digital Marketing', label: 'Digital Marketing' },
              { value: 'Graphic Designing', label: 'Graphic Designing' },
              { value: 'UI/UX Designing', label: 'UI/UX Designing' },
              { value: 'Not sure yet', label: 'Not sure yet' },
            ] },
          ],
        },
      },
    },
  },
  {
    slug: 'machine-learning',
    title: 'Machine Learning',
    data: {
      meta: { documentTitle: 'Machine Learning Services | Databiqs' },
      hiddenSections: [],
      cardPreview: {
        icon: 'brain',
        shortDescription: 'Predictive models and deep learning algorithms trained on your proprietary data to uncover hidden insights and forecast trends.',
        features: ['Predictive Analytics', 'Pattern Recognition', 'Real-time Insights'],
      },
      hero: {
        kicker: 'Predictive Intelligence',
        breadcrumbCurrent: 'Machine Learning',
        title: 'Turn Business Data Into Proactive Decisions',
        lead: 'We build production-grade machine learning solutions that transform historical and real-time data into action — predictive models and deep learning trained on your proprietary data, designed for accuracy, interpretability and business adoption from day one.',
        primaryCtaLabel: bookCta.label,
        primaryCtaTo: bookCta.to,
        secondaryCtaLabel: 'See case studies',
        secondaryCtaTo: '/case-studies',
        tags: ['Predictive Analytics', 'Pattern Recognition', 'Real-time Insights', 'Model Explainability', 'Drift Monitoring'],
        glance: {
          statusLabel: 'Taking new projects',
          rows: [
            glanceRow('clock', 'First model timeline', '[6–12] weeks'),
            glanceRow('bolt', 'Projects start from', 'USD [X]'),
            glanceRow('users', 'Engagement', 'Fixed price · Dedicated team'),
            glanceRow('shield', 'Confidentiality', 'NDA on request'),
          ],
          ctaLabel: 'Get a Free Estimate',
        },
      },
      whatWeBuild: {
        kicker: 'What we build',
        title: 'From raw data to production ML you can trust',
        side: 'One team covers data readiness, model development, deployment and monitoring.',
        cards: [
          { icon: 'target', title: 'Predictive analytics', desc: 'Forecast demand, churn and risk with context-aware models.' },
          { icon: 'search', title: 'Pattern recognition', desc: 'Discover hidden patterns in your data that unlock new efficiency.' },
          { icon: 'bolt', title: 'Real-time scoring', desc: 'Deploy low-latency ML pipelines for in-the-moment decisions.' },
          { icon: 'shieldCheck', title: 'Bias checks & explainability', desc: 'Models your stakeholders can audit, trust and adopt.' },
          { icon: 'refresh', title: 'Drift monitoring & retraining', desc: 'Keep model accuracy high as real-world data shifts.' },
          { icon: 'cloud', title: 'Production deployment', desc: 'Scalable inference on AWS with CI/CD and observability.' },
        ],
      },
      whyDatabiqs: {
        kicker: 'Why Databiqs',
        title: 'Why teams choose Databiqs for machine learning',
        cards: [
          { icon: 'brain', title: 'Accuracy and interpretability', desc: 'Models stakeholders understand well enough to actually use.' },
          { icon: 'layers', title: 'One team, full stack', desc: 'Data engineering, modeling and deployment — no hand-offs.' },
          { icon: 'users', title: 'Scales with you', desc: 'Start with one model, grow to a dedicated ML team.' },
          { icon: 'code', title: 'Clean, owned code', desc: 'Tested, documented pipelines you keep in your own infrastructure.' },
        ],
      },
      toc: {
        eyebrow: 'On this page',
        items: [
          { id: 'ml-end-to-end', label: 'Machine learning services, end to end' },
          { id: 'ml-timeline-cost', label: 'First model: timeline and cost' },
          { id: 'ml-hire-team', label: 'Fixed-price model or a dedicated ML team' },
          { id: 'ml-data-readiness', label: 'Data readiness and feature strategy' },
        ],
        cardTitle: 'Need a quote?',
        cardDesc: 'Free 30-minute call, fixed quote after.',
        cardCtaLabel: 'Book a call',
        cardCtaTo: '/book-consultation',
      },
      longform: [
        {
          id: 'ml-end-to-end',
          title: 'Machine learning services, end to end',
          paragraphs: [
            'One team owns the whole pipeline: data readiness, feature engineering, model development, validation and production deployment — fewer hand-offs, faster iteration.',
            'We work in weekly sprints and review model performance with you every week, using real evaluation metrics, not just demo accuracy.',
          ],
        },
        {
          id: 'ml-timeline-cost',
          title: 'First model: timeline and cost',
          paragraphs: [
            'A focused first model — a forecast, a classifier, a recommender — typically takes [6–12] weeks and costs USD [X]–[Y], depending on data readiness and the complexity of the problem.',
          ],
        },
        {
          id: 'ml-hire-team',
          title: 'Fixed-price model or a dedicated ML team',
          paragraphs: [
            'A fixed-price engagement suits one clearly scoped model. A dedicated ML team suits products where models keep evolving — new features, new data sources, continuous retraining.',
          ],
        },
        {
          id: 'ml-data-readiness',
          title: 'Data readiness and feature strategy',
          paragraphs: [
            'Most ML projects succeed or fail on data readiness. We start with a data audit and a feature strategy tied to the business KPI you actually care about, before any model is trained.',
          ],
        },
      ],
      process: {
        kicker: 'How we work',
        title: 'A clear process, with a demo every week',
        ctaLabel: bookCta.label,
        ctaTo: bookCta.to,
        steps: [
          { num: '01', week: 'Week 1', title: 'Data readiness audit', desc: 'Assess data quality and define a feature strategy aligned to KPIs.' },
          { num: '02', week: 'Weeks 2–4', title: 'Model development', desc: 'Build and validate models with bias checks and explainability tooling.' },
          { num: '03', week: 'Weeks 5–8', title: 'Deployment', desc: 'Production deployment with a live demo and review every Friday.' },
          { num: '04', week: 'Ongoing', title: 'Monitor & retrain', desc: 'Drift monitoring and retraining routines to keep accuracy high.' },
        ],
      },
      techStack: {
        kicker: 'Tech stack',
        title: 'Built on tools that scale',
        items: [
          { name: 'Python', logoKey: 'python' },
          { name: 'PostgreSQL', logoKey: 'postgresql' },
          { name: 'AWS', logoKey: 'aws' },
          { name: 'Docker', logoKey: 'docker' },
        ],
      },
      caseStudy: {
        kicker: 'Featured case study',
        imageSrc: '',
        imageAlt: 'Product screenshot — ML model built by Databiqs',
        projectName: '[Project name]',
        meta: 'Predictive model · Real-time scoring',
        sub: '[Industry] company, [size] — named with permission, or "name withheld under NDA".',
        challengeLabel: 'Challenge',
        challengeText: '[The problem the client had, in one or two sentences.]',
        solutionLabel: 'What we built',
        solutionText: '[The solution, stack and timeline.]',
        results: [
          { value: '[Result]', label: 'e.g. forecast accuracy improved' },
          { value: '[Result]', label: 'e.g. churn reduced' },
        ],
        linkLabel: 'Read the full case study',
        linkTo: '/case-studies',
      },
      engagement: {
        kicker: 'Engagement models',
        title: 'Pick how you want to work with us',
        noteText: 'ML projects typically start from USD [X]',
        tiers: [
          { icon: 'file', title: 'Fixed-price project', desc: 'One clearly scoped model with a fixed quote after a free call.', bullets: ['Fixed scope and timeline', 'Weekly demos', 'Pay by milestone'], highlight: false, tag: '' },
          { icon: 'users', title: 'Dedicated ML team', desc: 'Senior data scientists and engineers who keep evolving your models, billed monthly.', bullets: ['New features and data sources', 'Works in your tools', 'Monthly billing'], highlight: true, tag: 'Scale with us' },
          { icon: 'clock', title: 'Hourly', desc: 'For audits, retraining and ongoing support after deployment.', bullets: ['No minimum commitment', 'Transparent time logs', 'Senior engineers only'], highlight: false, tag: '' },
        ],
      },
      faq: {
        kicker: 'FAQ',
        title: 'Questions, answered',
        lead: 'Still have a question? Ask us directly on a free 30-minute call.',
        ctaLabel: bookCta.label,
        ctaTo: bookCta.to,
        items: [
          { question: 'How much does a machine learning model cost?', answer: 'Most first models cost between USD [X] and USD [Y], depending on data readiness and problem complexity. You get a fixed quote after a free call.' },
          { question: 'Do you need our data to be perfectly clean first?', answer: 'No — we start with a data readiness audit and work with what you have, flagging gaps that matter versus ones that can wait.' },
          { question: 'How do you make sure the model is trustworthy?', answer: 'Validation, bias checks and explainability tooling are part of every build, so stakeholders can see why the model made a given prediction.' },
          { question: 'Fixed price or dedicated team — which is right for me?', answer: 'Choose fixed price for one clearly scoped model. Choose a dedicated team if models and data sources will keep evolving.' },
          { question: 'Who owns the models and pipelines?', answer: 'You do. All code, trained artifacts and documentation are handed over and live in your own infrastructure.' },
          { question: 'How do you prevent model performance from degrading over time?', answer: 'Drift monitoring tracks real-world performance after launch, with retraining routines to keep accuracy high.' },
        ],
      },
      related: {
        kicker: 'Related services',
        title: 'Explore more ways we can help',
        items: [
          { icon: 'workflow', title: 'AI Automation', desc: 'Workflow orchestration and process automation', to: '/services/ai-automation' },
          { icon: 'lightbulb', title: 'AI Consulting', desc: 'Roadmaps and ROI-driven AI adoption', to: '/services/ai-consulting' },
          { icon: 'calendar', title: 'Free consultation', desc: '30 minutes with a senior engineer', to: '/book-consultation' },
        ],
      },
      blog: {
        kicker: 'From the blog',
        title: 'Guides and answers',
        allLabel: 'All articles',
        allTo: '/blog-page',
        posts: [
          { tag: 'ML', cover: 'model accuracy', imageSrc: '', title: 'How much data do you really need for ML?', to: '/blog-page', readTime: '[X] min read', linkLabel: 'Read the guide' },
          { tag: 'MLOps', cover: 'drift monitoring', imageSrc: '', title: 'Model drift: what it is and how to catch it', to: '/blog-page', readTime: '[X] min read', linkLabel: 'Read the guide' },
          { tag: 'AI', cover: 'explainability', imageSrc: '', title: 'Why model explainability matters for adoption', to: '/blog-page', readTime: '[X] min read', linkLabel: 'Read the guide' },
        ],
      },
      finalCta: {
        kicker: 'Start a project',
        title: 'Plan your first ML model with us',
        lead: 'Share a few details and a senior engineer will reply within 24 hours.',
        bullets: ['Fixed quote after a free 30-minute call', 'NDA on request', 'You own 100% of the code'],
        form: {
          badgeText: 'Senior engineer reviews every request',
          title: 'Get a free project estimate',
          sub: 'Tell us a little about your project. We reply within 24 hours.',
          submitLabel: 'Get my free estimate',
          privacyText: 'Your details stay confidential and are never shared or sold. NDA available on request.',
          fields: [
            { key: 'fullName', type: 'text', label: 'Full name', placeholder: 'Jane Smith', required: true },
            { key: 'email', type: 'email', label: 'Work email', placeholder: 'jane@company.com', required: true },
            { key: 'company', type: 'text', label: 'Company name', placeholder: 'Company Inc.', required: false },
            { key: 'phone', type: 'tel', label: 'Phone number', placeholder: '+1 555 000 0000', required: false },
            { key: 'services', type: 'select', label: 'Services', required: false, options: [
              { value: 'Full-Stack & SaaS Development', label: 'Full-Stack & SaaS Development' },
              { value: 'AI Chatbots', label: 'AI Chatbots' },
              { value: 'AI Automation', label: 'AI Automation' },
              { value: 'Machine Learning', label: 'Machine Learning' },
              { value: 'AI Consulting', label: 'AI Consulting' },
              { value: 'Mobile App Development', label: 'Mobile App Development' },
              { value: 'Digital Marketing', label: 'Digital Marketing' },
              { value: 'Graphic Designing', label: 'Graphic Designing' },
              { value: 'UI/UX Designing', label: 'UI/UX Designing' },
              { value: 'Not sure yet', label: 'Not sure yet' },
            ] },
          ],
        },
      },
    },
  },
  {
    slug: 'ai-consulting',
    title: 'AI Consulting',
    data: {
      meta: { documentTitle: 'AI Consulting Services | Databiqs' },
      hiddenSections: [],
      cardPreview: {
        icon: 'compass',
        shortDescription: 'Comprehensive roadmaps for AI adoption. We assess readiness, identify high-impact use cases, and architect scalable solutions.',
        features: ['Roadmap Planning', 'ROI Analysis', 'Implementation Support'],
      },
      hero: {
        kicker: 'Strategic AI Advisory',
        breadcrumbCurrent: 'AI Consulting',
        title: 'Align AI Investments With Measurable Outcomes',
        lead: 'Comprehensive roadmaps for AI adoption. We assess readiness, identify high-impact use cases, and architect scalable solutions — connecting technical feasibility, organizational readiness and ROI into a practical transformation roadmap.',
        primaryCtaLabel: bookCta.label,
        primaryCtaTo: bookCta.to,
        secondaryCtaLabel: 'See case studies',
        secondaryCtaTo: '/case-studies',
        tags: ['Roadmap Planning', 'ROI Analysis', 'Implementation Support', 'Readiness Assessment', 'Governance'],
        glance: {
          statusLabel: 'Taking new engagements',
          rows: [
            glanceRow('clock', 'Roadmap timeline', '[2–4] weeks'),
            glanceRow('bolt', 'Engagements start from', 'USD [X]'),
            glanceRow('users', 'Engagement', 'Fixed scope · Advisory retainer'),
            glanceRow('shield', 'Confidentiality', 'NDA on request'),
          ],
          ctaLabel: 'Get a Free Estimate',
        },
      },
      whatWeBuild: {
        kicker: 'What we deliver',
        title: 'A practical roadmap, not a slide deck',
        side: 'One team connects technical feasibility, readiness and ROI into a plan you can execute.',
        cards: [
          { icon: 'compass', title: 'Roadmap planning', desc: 'A phased plan with clear milestones and ownership.' },
          { icon: 'target', title: 'ROI analysis', desc: 'Prioritize use cases by impact, complexity and time-to-value.' },
          { icon: 'wrench', title: 'Implementation support', desc: 'Hands-on help turning the roadmap into shipped systems.' },
          { icon: 'search', title: 'Readiness assessment', desc: 'An honest look at your data, tooling, teams and governance.' },
          { icon: 'shieldCheck', title: 'Governance & policy', desc: 'Guardrails that de-risk AI initiatives as they scale.' },
          { icon: 'lightbulb', title: 'Executive workshops', desc: 'Align leadership and stakeholders on priorities and constraints.' },
        ],
      },
      whyDatabiqs: {
        kicker: 'Why Databiqs',
        title: 'Why leadership teams choose Databiqs for AI strategy',
        cards: [
          { icon: 'compass', title: 'Practical, not theoretical', desc: 'Every recommendation is grounded in what we can actually build.' },
          { icon: 'layers', title: 'Strategy and delivery, one team', desc: 'The team that advises you can also build it — no hand-off risk.' },
          { icon: 'users', title: 'Built for your organization', desc: 'Roadmaps shaped by your data, teams and constraints, not generic playbooks.' },
          { icon: 'shieldCheck', title: 'De-risked by design', desc: 'Architecture, policy and operating-model clarity before you invest.' },
        ],
      },
      toc: {
        eyebrow: 'On this page',
        items: [
          { id: 'con-end-to-end', label: 'AI consulting services, end to end' },
          { id: 'con-timeline-cost', label: 'AI roadmap: timeline and cost' },
          { id: 'con-engagement-model', label: 'Fixed-scope roadmap or an ongoing advisory retainer' },
          { id: 'con-readiness', label: 'Readiness assessment: data, tooling, teams, governance' },
        ],
        cardTitle: 'Need a quote?',
        cardDesc: 'Free 30-minute call, fixed quote after.',
        cardCtaLabel: 'Book a call',
        cardCtaTo: '/book-consultation',
      },
      longform: [
        {
          id: 'con-end-to-end',
          title: 'AI consulting services, end to end',
          paragraphs: [
            'We help leadership teams prioritize where AI creates the most value, then connect that priority list to a phased delivery plan with clear milestones and governance.',
            'Because the same team can also build what we recommend, the roadmap stays grounded in what is technically and organizationally realistic — not a wishlist.',
          ],
        },
        {
          id: 'con-timeline-cost',
          title: 'AI roadmap: timeline and cost',
          paragraphs: [
            'A focused readiness assessment and roadmap typically takes [2–4] weeks and costs USD [X]–[Y], depending on the number of stakeholders and use cases evaluated.',
          ],
        },
        {
          id: 'con-engagement-model',
          title: 'Fixed-scope roadmap or an ongoing advisory retainer',
          paragraphs: [
            'A fixed-scope engagement suits a one-time strategic review. An advisory retainer suits organizations that want continued guidance as they execute and new opportunities emerge.',
          ],
        },
        {
          id: 'con-readiness',
          title: 'Readiness assessment: data, tooling, teams, governance',
          paragraphs: [
            'We assess data quality and availability, existing tooling, team capability and governance maturity — the four things that most often determine whether an AI initiative actually ships.',
          ],
        },
      ],
      process: {
        kicker: 'How we work',
        title: 'A clear process, with milestones you can see',
        ctaLabel: bookCta.label,
        ctaTo: bookCta.to,
        steps: [
          { num: '01', week: 'Week 1', title: 'Executive workshops', desc: 'Define priorities, constraints and success metrics with stakeholders.' },
          { num: '02', week: 'Weeks 2–3', title: 'Readiness assessment', desc: 'Evaluate data, tooling, teams and governance maturity.' },
          { num: '03', week: 'Week 4', title: 'Roadmap delivery', desc: 'A phased implementation plan with milestones and ownership.' },
          { num: '04', week: 'Ongoing', title: 'Implementation support', desc: 'Hands-on help turning the roadmap into shipped systems.' },
        ],
      },
      techStack: {
        kicker: 'Tooling we assess and recommend on',
        title: 'Technology-agnostic, outcome-driven',
        items: [
          { name: 'Python', logoKey: 'python' },
          { name: 'AWS', logoKey: 'aws' },
          { name: 'Docker', logoKey: 'docker' },
        ],
      },
      caseStudy: {
        kicker: 'Featured case study',
        imageSrc: '',
        imageAlt: 'AI roadmap delivered by Databiqs',
        projectName: '[Project name]',
        meta: 'AI readiness assessment · Roadmap',
        sub: '[Industry] company, [size] — named with permission, or "name withheld under NDA".',
        challengeLabel: 'Challenge',
        challengeText: '[The problem the client had, in one or two sentences.]',
        solutionLabel: 'What we delivered',
        solutionText: '[The roadmap, priorities and timeline.]',
        results: [
          { value: '[Result]', label: 'e.g. use cases prioritized' },
          { value: '[Result]', label: 'e.g. roadmap approved by leadership' },
        ],
        linkLabel: 'Read the full case study',
        linkTo: '/case-studies',
      },
      engagement: {
        kicker: 'Engagement models',
        title: 'Pick how you want to work with us',
        noteText: 'Roadmap engagements typically start from USD [X]',
        tiers: [
          { icon: 'file', title: 'Fixed-scope roadmap', desc: 'A one-time readiness assessment and roadmap with a fixed quote.', bullets: ['Fixed scope and timeline', 'Executive workshops included', 'Pay by milestone'], highlight: false, tag: '' },
          { icon: 'users', title: 'Advisory retainer', desc: 'Ongoing strategic guidance as you execute the roadmap, billed monthly.', bullets: ['Continued prioritization', 'Works with your leadership team', 'Monthly billing'], highlight: true, tag: 'Scale with us' },
          { icon: 'clock', title: 'Hourly', desc: 'For one-off workshops, reviews and second opinions.', bullets: ['No minimum commitment', 'Transparent time logs', 'Senior advisors only'], highlight: false, tag: '' },
        ],
      },
      faq: {
        kicker: 'FAQ',
        title: 'Questions, answered',
        lead: 'Still have a question? Ask us directly on a free 30-minute call.',
        ctaLabel: bookCta.label,
        ctaTo: bookCta.to,
        items: [
          { question: 'How much does an AI roadmap cost?', answer: 'Most readiness assessments and roadmaps cost between USD [X] and USD [Y], depending on scope. You get a fixed quote after a free call.' },
          { question: 'Can you also build what you recommend?', answer: 'Yes — the same team can implement the roadmap, or support your internal team through implementation.' },
          { question: 'How fast can you start?', answer: 'Most engagements kick off within [1–2] weeks of the discovery call, once scope and stakeholders are confirmed.' },
          { question: 'Fixed scope or retainer — which is right for me?', answer: 'Choose a fixed-scope roadmap for a one-time strategic review. Choose a retainer if you want continued guidance through execution.' },
          { question: 'Do you work with our existing tools and vendors?', answer: 'Yes — our recommendations are technology-agnostic and work with the tools and vendors you already use where it makes sense.' },
          { question: 'Who owns the roadmap and findings?', answer: 'You do. All deliverables, documentation and findings are handed over and are yours to keep and act on.' },
        ],
      },
      related: {
        kicker: 'Related services',
        title: 'Explore more ways we can help',
        items: [
          { icon: 'chip', title: 'Machine Learning', desc: 'Predictive models trained on your data', to: '/services/machine-learning' },
          { icon: 'workflow', title: 'AI Automation', desc: 'Workflow orchestration and process automation', to: '/services/ai-automation' },
          { icon: 'calendar', title: 'Free consultation', desc: '30 minutes with a senior engineer', to: '/book-consultation' },
        ],
      },
      blog: {
        kicker: 'From the blog',
        title: 'Guides and answers',
        allLabel: 'All articles',
        allTo: '/blog-page',
        posts: [
          { tag: 'Strategy', cover: 'AI readiness', imageSrc: '', title: 'How to run an AI readiness assessment', to: '/blog-page', readTime: '[X] min read', linkLabel: 'Read the guide' },
          { tag: 'ROI', cover: 'prioritization', imageSrc: '', title: 'Prioritizing AI use cases by ROI', to: '/blog-page', readTime: '[X] min read', linkLabel: 'Read the guide' },
          { tag: 'Governance', cover: 'AI policy', imageSrc: '', title: 'AI governance basics for growing teams', to: '/blog-page', readTime: '[X] min read', linkLabel: 'Read the guide' },
        ],
      },
      finalCta: {
        kicker: 'Start a project',
        title: 'Plan your AI roadmap with us',
        lead: 'Share a few details and a senior advisor will reply within 24 hours.',
        bullets: ['Fixed quote after a free 30-minute call', 'NDA on request', 'You own every deliverable'],
        form: {
          badgeText: 'Senior advisor reviews every request',
          title: 'Get a free roadmap estimate',
          sub: 'Tell us a little about your goals. We reply within 24 hours.',
          submitLabel: 'Get my free estimate',
          privacyText: 'Your details stay confidential and are never shared or sold. NDA available on request.',
          fields: [
            { key: 'fullName', type: 'text', label: 'Full name', placeholder: 'Jane Smith', required: true },
            { key: 'email', type: 'email', label: 'Work email', placeholder: 'jane@company.com', required: true },
            { key: 'company', type: 'text', label: 'Company name', placeholder: 'Company Inc.', required: false },
            { key: 'phone', type: 'tel', label: 'Phone number', placeholder: '+1 555 000 0000', required: false },
            { key: 'services', type: 'select', label: 'Services', required: false, options: [
              { value: 'Full-Stack & SaaS Development', label: 'Full-Stack & SaaS Development' },
              { value: 'AI Chatbots', label: 'AI Chatbots' },
              { value: 'AI Automation', label: 'AI Automation' },
              { value: 'Machine Learning', label: 'Machine Learning' },
              { value: 'AI Consulting', label: 'AI Consulting' },
              { value: 'Mobile App Development', label: 'Mobile App Development' },
              { value: 'Digital Marketing', label: 'Digital Marketing' },
              { value: 'Graphic Designing', label: 'Graphic Designing' },
              { value: 'UI/UX Designing', label: 'UI/UX Designing' },
              { value: 'Not sure yet', label: 'Not sure yet' },
            ] },
          ],
        },
      },
    },
  },
];
